import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Row, Col, Button, Badge, Table, Spinner, Alert, Form } from 'react-bootstrap';
import { FaShoppingCart, FaWhatsapp, FaExclamationTriangle, FaTruck, FaChevronDown, FaChevronUp, FaExchangeAlt, FaFilter, FaRedo } from 'react-icons/fa';
import { fetchRestockSuggestions } from '../../redux/features/purchase.features';
import { fetchSuppliers } from '../../redux/features/supplier.features';
import SupplierField from '../core/shared/fields/SupplierField';
import { formatCurrency } from '../../utility';
import { sendWhatsApp, WhatsAppFormatter } from '../../utility/WhatsAppFormatter';
import { PDFFormatter } from '../../utility/PDFFormatter';
import { useNavigate } from 'react-router-dom';
import { fetchCompanies } from '../../redux/features/company.features';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

import UnifiedRestockFilter from './filters/UnifiedRestockFilter';

const BRAND_COLOR = '#001c3a';

const RestockQueue = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { restockSuggestions, status, purchases } = useSelector(state => state.purchaseReducer);
    const { suppliers } = useSelector(state => state.supplierReducer);
    const { companies } = useSelector(state => state.companyReducer);
    const { user } = useSelector(state => state.authReducer);

    // Local state to manage selections and overrides per suggestion group
    const [selections, setSelections] = useState({});
    const [overrides, setOverrides] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});
    const [supplierOverrides, setSupplierOverrides] = useState({}); // { [idx]: { supplierId, supplierName, agentId, agentName } }
    const [isChangingSupplier, setIsChangingSupplier] = useState({}); // { [idx]: boolean }
    const [stockFilter, setStockFilter] = useState({ id: 'reorder', operator: 'reorder', value: null });
    const [bulkOrderQty, setBulkOrderQty] = useState('');
    const [hiddenItemIds, setHiddenItemIds] = useState([]);
    const [activeCategory, setActiveCategory] = useState('All');

    useEffect(() => {
        dispatch(fetchRestockSuggestions());
        dispatch(fetchSuppliers());
        dispatch(fetchCompanies());
    }, [dispatch]);

    // Flat list of all raw items for global filter context
    const allItems = React.useMemo(() => {
        if (!restockSuggestions) return [];
        return restockSuggestions.reduce((acc, group) => [...acc, ...group.items], []);
    }, [restockSuggestions]);

    const handleHideItem = (itemId) => {
        setHiddenItemIds(prev => [...prev, itemId]);
        toast.info("Item hidden from current restock view");
    };

    // List of unique categories from all items
    const categories = React.useMemo(() => {
        if (!allItems) return ['All'];
        const cats = Array.from(new Set(allItems.map(i => i.category).filter(Boolean)));
        return ['All', ...cats.sort()];
    }, [allItems]);

    // Filtered suggestions based on threshold, category, and hidden items
    const filteredSuggestions = React.useMemo(() => {
        if (!restockSuggestions) return [];

        return restockSuggestions.map(group => {
            const filteredItems = group.items.filter(item => {
                // 1. Hide manually excluded items
                if (hiddenItemIds.includes(item._id)) return false;

                // 2. Category Filter
                if (activeCategory !== 'All' && item.category !== activeCategory) return false;

                // 3. Stock Threshold Filter
                const stock = item.stockTracking?.availableForSale || 0;
                const { operator, value } = stockFilter;

                if (operator === 'reorder') {
                    const reorder = item.stockTracking?.reorderPoint || 0;
                    return stock < reorder;
                }

                switch (operator) {
                    case '=': return stock === value;
                    case '<': return stock < value;
                    case '<=': return stock <= value;
                    case '>': return stock > value;
                    case '>=': return stock >= value;
                    default: return true;
                }
            });

            return { ...group, items: filteredItems };
        }).filter(group => group.items.length > 0);
    }, [restockSuggestions, stockFilter, activeCategory, hiddenItemIds]);

    // Initialize state when suggestions load
    useEffect(() => {
        if (filteredSuggestions?.length) {
            const initialSelections = {};
            const initialOverrides = {};
            filteredSuggestions.forEach((group, idx) => {
                const groupKey = `${idx}`; // Using index as simple key for UI state
                initialSelections[groupKey] = group.items.map(i => i._id); // All checked by default
                initialOverrides[groupKey] = {}; // empty
                group.items.forEach(item => {
                    const qty = item.stockTracking?.reorderPoint ? (item.stockTracking.reorderPoint * 2) : 10;
                    initialOverrides[groupKey][item._id] = qty;
                });
            });
            setSelections(initialSelections);
            setOverrides(initialOverrides);
        }
    }, [filteredSuggestions]);

    const toggleSelection = (groupKey, itemId) => {
        setSelections(prev => {
            const groupSelections = prev[groupKey] || [];
            if (groupSelections.includes(itemId)) {
                return { ...prev, [groupKey]: groupSelections.filter(id => id !== itemId) };
            } else {
                return { ...prev, [groupKey]: [...groupSelections, itemId] };
            }
        });
    };

    const toggleAll = (groupKey, allItemIds) => {
        setSelections(prev => {
            const current = prev[groupKey] || [];
            if (current.length === allItemIds.length) {
                return { ...prev, [groupKey]: [] }; // Uncheck all
            } else {
                return { ...prev, [groupKey]: allItemIds }; // Check all
            }
        });
    };

    const handleQtyChange = (groupKey, itemId, val) => {
        const qty = parseInt(val) || 0;
        setOverrides(prev => ({
            ...prev,
            [groupKey]: {
                ...prev[groupKey],
                [itemId]: qty
            }
        }));
    };

    const toggleExpand = (idx) => {
        setExpandedGroups(prev => ({ ...prev, [idx]: !prev[idx] }));
    };

    const handleSupplierChange = (idx, selectedSupplierId) => {
        if (!selectedSupplierId) {
            setSupplierOverrides(prev => {
                const updated = { ...prev };
                delete updated[idx];
                return updated;
            });
            return;
        }

        const supplier = suppliers.find(s => s._id === selectedSupplierId);
        if (supplier) {
            // Check for primary agent first
            const primaryAgent = supplier.contacts?.find(c => c.isPrimary);

            setSupplierOverrides(prev => ({
                ...prev,
                [idx]: {
                    supplierId: supplier._id,
                    supplierName: supplier.business_name,
                    agentId: primaryAgent?._id || null,
                    agentName: primaryAgent?.name || '',
                    // PRIORITY: Primary Agent Phone > Business Phone
                    contactNumber: primaryAgent?.phone || supplier.contact_info?.contact_number || ''
                }
            }));
            setIsChangingSupplier(prev => ({ ...prev, [idx]: false }));
        }
    };

    const handleAgentOverride = (idx, agentId) => {
        const currentOverride = supplierOverrides[idx] || restockSuggestions[idx];
        const selectedSupplier = suppliers.find(s => s._id === currentOverride.supplierId);

        if (!agentId) {
            // Revert to Main Supplier Number if "Direct" is selected
            setSupplierOverrides(prev => ({
                ...prev,
                [idx]: {
                    ...currentOverride,
                    agentId: null,
                    agentName: '',
                    contactNumber: selectedSupplier?.contact_info?.contact_number || ''
                }
            }));
            return;
        }

        const agent = selectedSupplier?.contacts?.find(c => c._id === agentId);

        setSupplierOverrides(prev => ({
            ...prev,
            [idx]: {
                ...currentOverride,
                agentId: agentId || null,
                agentName: agent ? agent.name : '',
                contactNumber: agent ? agent.phone : (selectedSupplier?.contact_info?.contact_number || '')
            }
        }));
    };

    const handleCreateDraft = (suggestion, idx) => {
        const groupKey = `${idx}`;
        const selectedIds = selections[groupKey] || [];
        const itemOverrides = overrides[groupKey] || {};
        const supplierInfo = supplierOverrides[idx] || suggestion;

        if (selectedIds.length === 0) return;

        // Filter valid items
        const validItems = suggestion.items.filter(it => selectedIds.includes(it._id));

        // Encode items using the STRICT 11-KEY STRUCTURE required by UnifiedPurchaseForm
        const items = validItems.map(it => {
            const purchaseQty = itemOverrides[it._id]; // User override
            const unitCost = it.lastUnitCost || it.costPrice || 0;
            const isSer = it.isSerialized || it.serialized || it.category === 'Device';

            return {
                item_id: it._id,
                variant_id: it.variant_id || null, // Important if the suggestion is a specific variant
                itemName: it.itemName,
                itemImage: it.itemImage, // Optional but good for UI

                purchaseQty: purchaseQty,
                unitCost: unitCost,
                sellingPrice: it.pricing?.sellingPrice || 0,
                total_price: purchaseQty * unitCost, // Mandatory for initial calculation

                variantAttributes: it.variantAttributes || [],

                isSerialized: isSer,
                // Initialize array to prevent "map of undefined" errors in the Form
                serializedItems: isSer ? Array(purchaseQty).fill({
                    serialNumber: "",
                    unitCost: unitCost,
                    sellingPrice: it.pricing?.sellingPrice || 0,
                    condition: "Brand New"
                }) : [],

                condition: "Brand New"
            };
        });

        // We'll pass this via state or session storage as it's too big for URL
        sessionStorage.setItem('restock_prefill', JSON.stringify(items));
        navigate(`/purchases/new?type=po&mode=restock&supplierId=${supplierInfo.supplierId || ''}&agentId=${supplierInfo.agentId || ''}&agentName=${encodeURIComponent(supplierInfo.agentName || '')}`);
    };

    const handleBulkQtyApply = () => {
        const qty = parseInt(bulkOrderQty);
        if (isNaN(qty)) return;

        const updatedOverrides = { ...overrides };
        filteredSuggestions.forEach((group, idx) => {
            const groupKey = `${idx}`;
            const selectedIds = selections[groupKey] || [];
            if (!updatedOverrides[groupKey]) updatedOverrides[groupKey] = {};
            selectedIds.forEach(id => {
                updatedOverrides[groupKey][id] = qty;
            });
        });
        setOverrides(updatedOverrides);
        toast.success(`Updated quantity to ${qty} for all selected items`);
    };

    const handleExportExcel = () => {
        const data = [];
        filteredSuggestions.forEach(group => {
            group.items.forEach(it => {
                data.push({
                    'Supplier': group.supplierName,
                    'Item Name': it.itemName,
                    'Barcode': it.barcode,
                    'Current Stock': it.stockTracking?.availableForSale || 0,
                    'Reorder Point': it.stockTracking?.reorderPoint || 0,
                    'Target Qty': overrides[`${filteredSuggestions.indexOf(group)}`]?.[it._id] || (it.stockTracking?.reorderPoint * 2 || 10),
                    'Est. Cost': (it.lastUnitCost || 0) * (overrides[`${filteredSuggestions.indexOf(group)}`]?.[it._id] || 0)
                });
            });
        });

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Restock Queue");
        XLSX.writeFile(wb, `Restock_Queue_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handlePrintMasterPDF = () => {
        const company = companies?.[0];

        // Prepare data for the engine
        const groupedData = filteredSuggestions.map((group, idx) => ({
            supplierName: group.supplierName,
            items: group.items.map(it => ({
                itemName: it.itemName,
                stockTracking: it.stockTracking,
                targetQty: overrides[`${filteredSuggestions.indexOf(group)}`]?.[it._id] || 0
            }))
        }));

        PDFFormatter.generateMasterRestockList(groupedData, company, user);
    };

    const handlePrintSupplierRFQ = (suggestion, idx) => {
        const info = supplierOverrides[idx] || suggestion;
        const company = companies?.[0];

        // Prepare data for the engine with supplierId
        const rfqData = {
            supplierName: info.supplierName,
            agentName: info.agentName,
            supplierId: info.supplierId, // Pass supplierId for purchase count
            items: suggestion.items.filter(it => selections[`${idx}`]?.includes(it._id)).map(it => ({
                itemName: it.itemName,
                targetQty: overrides[`${idx}`]?.[it._id] || 0,
                unitCost: it.lastUnitCost || 0
            }))
        };

        // Pass actual purchases array for accurate count calculation
        PDFFormatter.generateRFQ(rfqData, company, user, purchases || []);
    };

    const handleWhatsAppRFQ = (suggestion, idx) => {
        const info = supplierOverrides[idx] || suggestion;
        const companyProfile = companies?.[0];

        const rfqData = {
            supplierName: info.supplierName,
            agentName: info.agentName,
            items: suggestion.items.filter(it => selections[idx]?.includes(it._id)).map(it => ({
                itemName: it.itemName,
                variantName: it.isVariant ? "Variant" : null,
                targetQty: overrides[idx]?.[it._id] || (it.stockTracking?.reorderPoint * 2 || 10),
                units: it.units || 'pcs'
            }))
        };

        const message = WhatsAppFormatter.generateRFQ(rfqData, companyProfile, user);
        const contactNo = info.contactNumber || '';
        sendWhatsApp(contactNo, message);
    };

    if (status === 'loading') {
        return (
            <div className="text-center p-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 text-muted">Analyzing stock levels...</p>
            </div>
        );
    }

    if (!restockSuggestions || restockSuggestions.length === 0) {
        return (
            <Alert variant="success" className="border-0 shadow-sm d-flex align-items-center gap-3 p-4">
                <div className="bg-success bg-opacity-10 p-3 rounded-circle text-success">
                    <FaTruck size={24} />
                </div>
                <div>
                    <h5 className="mb-1 fw-bold">All Stocked Up!</h5>
                    <p className="mb-0 text-muted">None of your inventory is currently below his reorder point.</p>
                </div>
            </Alert>
        );
    }

    return (
        <div className="restock-queue">
            {/* ENGAGING HEADER */}
            <div className="d-flex align-items-center justify-content-between mb-4 bg-white p-4 rounded-3 shadow-sm border-start border-5 border-warning">
                <div>
                    <h4 className="fw-bold text-dark mb-1">
                        <FaExclamationTriangle className="text-warning me-2" />
                        Restock Required
                    </h4>
                    <p className="text-muted mb-0 small">
                        The following items have dipped below their reorder points.
                        Review stock levels and generate purchase orders to maintain availability.
                    </p>
                </div>
                <div className="text-end">
                    <h2 className="fw-bold text-dark mb-0">{filteredSuggestions.reduce((acc, g) => acc + g.items.length, 0)}</h2>
                    <span className="badge bg-light text-muted border">Items Pending</span>
                </div>
            </div>

            <UnifiedRestockFilter
                items={allItems}
                onFilterChange={(f) => setStockFilter(f)}
                categories={categories}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
                onResetHidden={() => setHiddenItemIds([])}
                hiddenCount={hiddenItemIds.length}
            />

            {/* CATEGORY & PRUNING FILTER */}
            {/* CATEGORY & PRUNING FILTER */}


            {/* GLOBAL ACTIONS & BULK UPDATE */}
            <Card className="mb-4 border-0 shadow-sm bg-gradient-brand">
                <Card.Body className="p-3 d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                        <div className=" bg-opacity-10 p-2 rounded text-white">
                            <FaExchangeAlt />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="text-white small fw-bold text-uppercase">Bulk Qty for Selected:</span>
                            <Form.Control
                                type="number"
                                size="sm"
                                placeholder="Qty"
                                style={{ width: '80px' }}
                                value={bulkOrderQty}
                                onChange={(e) => setBulkOrderQty(e.target.value)}
                            />
                            <Button variant="light" size="sm" className="fw-bold px-3" onClick={handleBulkQtyApply}>
                                Apply All
                            </Button>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        <Button variant="outline-light" size="sm" className="fw-bold d-flex align-items-center gap-2 px-3" onClick={handleExportExcel}>
                            <FaTruck size={14} /> Export Excel
                        </Button>
                        <Button variant="outline-light" size="sm" className="fw-bold d-flex align-items-center gap-2 px-3" onClick={handlePrintMasterPDF}>
                            <FaExclamationTriangle size={14} /> Master Restock PDF
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <style>{`
                .bg-gradient-brand { background: linear-gradient(135deg, ${BRAND_COLOR} 0%, #003366 100%); }
                .text-white { color: white !important; }
            `}</style>

            <Row className="g-4">
                {filteredSuggestions.length === 0 ? (
                    <Col md={12}>
                        <Alert variant="info" className="border-0 shadow-sm">
                            No items match the selected stock threshold filter.
                        </Alert>
                    </Col>
                ) : (
                    filteredSuggestions.map((suggestion, idx) => {
                        const groupKey = `${idx}`;
                        const selectedCount = selections[groupKey]?.length || 0;
                        const isExpanded = expandedGroups[idx];
                        const changing = isChangingSupplier[idx];
                        const info = supplierOverrides[idx] || suggestion;

                        return (
                            <Col lg={12} key={idx}>
                                <Card className="border-0 shadow-sm overflow-hidden">
                                    <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
                                        <div className="d-flex align-items-center gap-3 flex-grow-1">
                                            <div className="bg-primary bg-opacity-10 p-2 rounded text-primary cursor-pointer" onClick={() => toggleExpand(idx)}>
                                                <FaTruck />
                                            </div>
                                            <div className="flex-grow-1">
                                                {changing ? (
                                                    <div className="d-flex gap-2 align-items-center" style={{ maxWidth: '400px' }}>
                                                        <SupplierField
                                                            suppliers={suppliers}
                                                            // Only pass the override ID if it exists; otherwise start empty (null)
                                                            selectedSupplierId={supplierOverrides[idx]?.supplierId || null}
                                                            onAddSupplier={(sId) => handleSupplierChange(idx, sId._id)}
                                                        />
                                                        <Button variant="link" size="sm" className="text-muted" onClick={() => setIsChangingSupplier(p => ({ ...p, [idx]: false }))}>Cancel</Button>
                                                    </div>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div>
                                                            <div className="fw-bold text-dark d-flex align-items-center gap-2">
                                                                {info.supplierName}
                                                                <Button variant="link" size="sm" className="p-0 text-muted" onClick={() => setIsChangingSupplier(p => ({ ...p, [idx]: true }))} title="Change Supplier">
                                                                    <FaExchangeAlt size={12} />
                                                                </Button>
                                                            </div>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div className="x-small text-muted" style={{ fontSize: '0.75rem' }}>
                                                                    via <span className="text-primary fw-bold text-uppercase">{info.agentName || 'Direct'}</span>
                                                                    {info.contactNumber && <span className="ms-2 text-success fw-bold">({info.contactNumber})</span>}
                                                                </div>
                                                                {(suppliers.find(s => s._id === info.supplierId)?.contacts?.length > 0) ? (
                                                                    <select
                                                                        className="border-0 bg-transparent text-primary fw-bold x-small p-0"
                                                                        style={{ fontSize: '0.7rem' }}
                                                                        value={info.agentId || ''}
                                                                        onChange={(e) => handleAgentOverride(idx, e.target.value)}
                                                                    >
                                                                        <option value="">(Change Agent)</option>
                                                                        {suppliers.find(s => s._id === info.supplierId)?.contacts?.map(c => (
                                                                            <option key={c._id} value={c._id}>{c.name}</option>
                                                                        ))}
                                                                    </select>
                                                                ) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-3">
                                            <Badge bg="danger" className="rounded-pill px-3">
                                                {suggestion.items.length} Low
                                            </Badge>
                                            <Button variant="light" size="sm" className="rounded-circle btn-icon" onClick={() => toggleExpand(idx)}>
                                                {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                            </Button>
                                        </div>
                                    </Card.Header>

                                    {/* Expandable Review Table directly in Card Body */}
                                    {(isExpanded || true) && (
                                        <Card.Body className="p-0">
                                            <Table hover size="sm" className="mb-0 align-middle">
                                                <thead className="bg-light text-muted small text-uppercase">
                                                    <tr>
                                                        <th className="ps-4" style={{ width: '40px' }}>
                                                            <Form.Check
                                                                checked={selectedCount === suggestion.items.length}
                                                                onChange={() => toggleAll(groupKey, suggestion.items.map(i => i._id))}
                                                            />
                                                        </th>
                                                        <th>Item Name</th>
                                                        <th style={{ width: '100px' }} className="text-center">Current</th>
                                                        <th style={{ width: '100px' }} className="text-center">Target</th>
                                                        <th style={{ width: '120px' }}>Order Qty</th>
                                                        <th className="text-end" style={{ width: '120px' }}>Est. Cost</th>
                                                        <th style={{ width: '40px' }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {suggestion.items.map((item) => {
                                                        const isChecked = selections[groupKey]?.includes(item._id);
                                                        const overrideQty = overrides[groupKey]?.[item._id] || 0;
                                                        const unitCost = item.lastUnitCost || item.costPrice || 0;

                                                        return (
                                                            <tr key={item._id} className={!isChecked ? 'opacity-50' : ''}>
                                                                <td className="ps-4">
                                                                    <Form.Check
                                                                        checked={isChecked}
                                                                        onChange={() => toggleSelection(groupKey, item._id)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div className="fw-bold text-dark small">{item.itemName}</div>
                                                                    {item.isVariant && <Badge bg="info" style={{ fontSize: '0.65rem' }}>Variant</Badge>}
                                                                </td>
                                                                <td className="text-center text-danger fw-bold">{item.stockTracking?.availableForSale}</td>
                                                                <td className="text-center text-muted">{item.stockTracking?.reorderPoint}</td>
                                                                <td>
                                                                    <Form.Control
                                                                        type="number"
                                                                        size="sm"
                                                                        value={overrideQty}
                                                                        onChange={(e) => handleQtyChange(groupKey, item._id, e.target.value)}
                                                                        className="text-center fw-bold text-primary"
                                                                        disabled={!isChecked}
                                                                    />
                                                                </td>
                                                                <td className="text-end fw-bold">
                                                                    {formatCurrency((item.lastUnitCost || 0) * overrideQty)}
                                                                </td>
                                                                <td className="text-center ps-0">
                                                                    <Button
                                                                        variant="link"
                                                                        size="sm"
                                                                        className="text-danger p-0"
                                                                        onClick={() => handleHideItem(item._id)}
                                                                        title="Hide item temporarily"
                                                                    >
                                                                        <FaChevronDown style={{ transform: 'rotate(90deg)' }} />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    )}

                                    <Card.Footer className="bg-light bg-opacity-50 border-top p-3">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div className="small text-muted">
                                                Selected: <strong>{selectedCount}</strong> items
                                            </div>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center justify-content-center gap-2 px-3 fw-bold"
                                                    onClick={() => handlePrintSupplierRFQ(suggestion, idx)}
                                                >
                                                    <FaExclamationTriangle size={14} /> Print RFQ
                                                </Button>
                                                <Button
                                                    variant="outline-success"
                                                    size="sm"
                                                    className="d-flex align-items-center justify-content-center gap-2 px-3 fw-bold"
                                                    onClick={() => handleWhatsAppRFQ(suggestion, idx)}
                                                >
                                                    <FaWhatsapp size={18} /> WhatsApp
                                                </Button>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    className="d-flex align-items-center justify-content-center gap-2 px-4 fw-bold"
                                                    style={{ backgroundColor: BRAND_COLOR, borderColor: BRAND_COLOR }}
                                                    onClick={() => handleCreateDraft(suggestion, idx)}
                                                    disabled={selectedCount === 0}
                                                >
                                                    <FaShoppingCart /> Create PO ({selectedCount})
                                                </Button>
                                            </div>
                                        </div>
                                    </Card.Footer>
                                </Card>
                            </Col>
                        );
                    })
                )}
            </Row>

            <style>{`
                .x-small { font-size: 0.85rem; }
                .cursor-pointer { cursor: pointer; }
                .btn-icon { width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; padding: 0; }
            `}</style>
        </div>
    );
};

export default RestockQueue;
